import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const NetworkContext = createContext({ isOnline: true });

export const useNetwork = () => useContext(NetworkContext);

const isNative = () => !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

/**
 * Single app-wide connectivity source of truth + API gate.
 *
 * Web: navigator.onLine + window online/offline events.
 * Native: @capacitor/network for the initial status, then the "network-change"
 * CustomEvent nativeBridge.js already dispatches from its own Network listener
 * (avoids registering a second competing native listener).
 *
 * Also installs the axios interceptors that make "all API requests respect
 * network state" true app-wide, without touching individual pages:
 *   - request: rejected immediately while offline (belt-and-suspenders — normally
 *     nothing is even mounted to issue a request while offline)
 *   - response: every failure is console.error-logged centrally, then re-thrown
 *     so existing per-page catch/toast handling is unaffected
 */
export const NetworkProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(() => (isNative() ? true : navigator.onLine));

  useEffect(() => {
    let cancelled = false;

    if (isNative()) {
      import('@capacitor/network').then(({ Network }) => {
        Network.getStatus().then((status) => {
          if (!cancelled) setIsOnline(status.connected);
        }).catch(() => {});
      }).catch(() => {});

      const handleNativeChange = (e) => setIsOnline(!!e.detail?.connected);
      document.addEventListener('network-change', handleNativeChange);
      return () => {
        cancelled = true;
        document.removeEventListener('network-change', handleNativeChange);
      };
    }

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Interceptors read the latest isOnline via a ref-like pattern (closure over
  // state would go stale) — re-registered whenever isOnline changes, with the
  // previous pair ejected first so exactly one pair is ever active.
  useEffect(() => {
    const requestId = axios.interceptors.request.use((config) => {
      if (!isOnline) {
        const offlineError = new Error('No internet connection');
        offlineError.__offline = true;
        return Promise.reject(offlineError);
      }
      return config;
    });

    const responseId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (!error.__offline) {
          console.error(
            '[API Error]',
            error.config?.method?.toUpperCase(), error.config?.url,
            error.response?.status, error.response?.data?.detail || error.message
          );
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
    };
  }, [isOnline]);

  return (
    <NetworkContext.Provider value={{ isOnline }}>
      {children}
    </NetworkContext.Provider>
  );
};

export default NetworkContext;
